"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import ClothingCategoryService, {
  CLOTHING_CATEGORY_STATUS,
  CLOTHING_CATEGORY_STATUS_NAME,
  ClothingCategory,
} from "../services/clothing-category-service";
import ClothingCategoriesTable, { ClothingCategoryRow } from "../components/clothing-categories-table";
import ClothingCategoryModal, { ClothingCategoryFormValues } from "../components/clothing-category-modal";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

function useDebounce<T>(value: T, delay = 500): T {
  const [val, setVal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setVal(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return val;
}

/** Extract array from API response */
function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const arr = raw.list ?? raw.data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

const ClothingCategoriesIndexPage: React.FC = () => {
  // State
  const [rows, setRows] = useState<ClothingCategoryRow[]>([]);
  const [filterStatus, setFilterStatus] = useState<number | undefined>();
  const [keySearch, setKeySearch] = useState("");
  const debouncedSearch = useDebounce(keySearch, 500);

  // Modals
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState<ClothingCategoryRow | null>(null);

  // Services
  const { fetch: listCategories, loading: loadingList } = ClothingCategoryService.list();
  const { fetch: createCategory, loading: creating } = ClothingCategoryService.create();
  const { fetch: updateCategory, loading: updating } = ClothingCategoryService.update();

  const listRef = useRef(listCategories);
  useEffect(() => { listRef.current = listCategories; }, [listCategories]);

  // Load data
  const load = useCallback(async () => {
    const res = await listRef.current({
      status: filterStatus,
      key_search: debouncedSearch || undefined,
    });
    if (res && isSuccess(res)) {
      const list = extractList(res.data);
      setRows(
        list.map((c: ClothingCategory) => ({
          id: c.id,
          name: c.name,
          code: c.code,
          sort_order: c.sort_order,
          status: c.status,
          created_at: c.created_at,
        }))
      );
    }
  }, [filterStatus, debouncedSearch]);

  useEffect(() => { void load(); }, [load]);

  // Handlers
  const onCreate = async (values: ClothingCategoryFormValues): Promise<boolean> => {
    try {
      const res = await createCategory({
        name: values.name,
        code: values.code,
        sort_order: values.sort_order,
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Tạo loại đồ thành công", type: "success" });
        void load();
        return true;
      }
      showToast({ message: res?.message || "Tạo loại đồ thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onCreate] Error:", err);
      showToast({ message: "Lỗi khi tạo loại đồ", type: "error" });
      return false;
    }
  };

  const onUpdate = async (values: ClothingCategoryFormValues): Promise<boolean> => {
    if (!editRow) return false;
    try {
      const res = await updateCategory(editRow.id, {
        name: values.name,
        code: values.code,
        sort_order: values.sort_order,
        status: values.status,
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Cập nhật loại đồ thành công", type: "success" });
        void load();
        return true;
      }
      showToast({ message: res?.message || "Cập nhật thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onUpdate] Error:", err);
      showToast({ message: "Lỗi khi cập nhật loại đồ", type: "error" });
      return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">
            Quản lý loại đồ giặt
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Quản lý danh sách loại đồ giặt, đồng bộ giữa web và app
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditRow(null);
            setOpenCreate(true);
          }}
          className="group inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white shadow-lg transition-all bg-green-600 shadow-green-200 hover:bg-green-700 hover:-translate-y-0.5 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM LOẠI ĐỒ</span>
        </button>
      </div>

      {/* ── MAIN CONTENT CARD ── */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">

        {/* ── TOOLBAR ── */}
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center justify-between gap-5">
          {/* Filter Group */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <select
              value={filterStatus ?? ""}
              onChange={(e) => setFilterStatus(e.target.value ? Number(e.target.value) : undefined)}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả trạng thái</option>
              {Object.entries(CLOTHING_CATEGORY_STATUS_NAME).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-[320px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              value={keySearch}
              onChange={(e) => setKeySearch(e.target.value)}
              placeholder="Tìm theo tên, mã loại đồ..."
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
            />
          </div>
        </div>

        {/* ── TABLE AREA ── */}
        <div className="p-0">
          <ClothingCategoriesTable
            rows={rows}
            isLoading={loadingList}
            onEdit={(row) => { setEditRow(row); setOpenCreate(true); }}
          />
        </div>

        {/* ── FOOTER ── */}
        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 font-medium">
            {rows.length > 0
              ? `Tổng cộng ${rows.length} loại đồ`
              : "Chưa có loại đồ nào"}
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      <ClothingCategoryModal
        isOpen={openCreate}
        onClose={() => { setOpenCreate(false); setEditRow(null); }}
        onSubmit={editRow ? onUpdate : onCreate}
        loading={creating || updating}
        editData={editRow}
      />
    </div>
  );
};

export default ClothingCategoriesIndexPage;
